import React, { useCallback, forwardRef, useRef, useImperativeHandle } from 'react';
import { View, Text } from 'react-native';
import DbInfiniteList from '../../DbInfiniteList';
import DbChatMessage from '../DbChatMessage';
import { styles } from './styles';
import type { DbChatListProps, DbChatListRef } from './types';
import type { ChatMessageData } from '../DbChatMessage/types';
import { TIME_DIVIDER_INTERVAL } from './types';

/** 判断是否需要插入时间分割线 */
const shouldShowTimeDivider = (current: ChatMessageData, prev?: ChatMessageData): boolean => {
    if (!prev) return true;
    return current.timestamp - prev.timestamp > TIME_DIVIDER_INTERVAL;
};

/** 格式化分割线时间 */
const formatDividerTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday =
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate();

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    if (isToday) {
        return `${hours}:${minutes}`;
    }

    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${month}-${day} ${hours}:${minutes}`;
};

const DbChatList = forwardRef<DbChatListRef, DbChatListProps>(({
    messages,
    showAvatar = true,
    showMyAvatar = true,
    showTime = true,
    showStatus = true,
    showSenderName = false,
    onRetry,
    onLongPress,
    onAvatarPress,
    onScroll,
    onScrollBeginDrag,
    onScrollEndDrag,
    style,
    ListHeaderComponent,
    ListFooterComponent,
}, ref) => {
    const listRef = useRef<DbChatListRef>(null);

    // 暴露 ref 方法
    useImperativeHandle(ref, () => ({
        scrollToOffset: (params) => listRef.current?.scrollToOffset(params),
        scrollToEnd: (params) => listRef.current?.scrollToEnd(params),
        scrollToTop: (animated) => listRef.current?.scrollToTop(animated),
        getFlatListRef: () => listRef.current?.getFlatListRef() ?? null,
    }), []);

    // renderItem 接收 (item, index) 格式
    const renderItem = useCallback((item: ChatMessageData, index: number) => {
        const prev = index > 0 ? messages[index - 1] : undefined;
        const needTimeDivider = shouldShowTimeDivider(item, prev);

        return (
            <View>
                {/* 时间分割线 */}
                {needTimeDivider && (
                    <View style={styles.timeDivider}>
                        <Text style={styles.timeDividerText}>
                            {formatDividerTime(item.timestamp)}
                        </Text>
                    </View>
                )}

                {/* 消息 */}
                <DbChatMessage
                    message={item}
                    showAvatar={showAvatar}
                    showMyAvatar={showMyAvatar}
                    showTime={showTime && !needTimeDivider}
                    showStatus={showStatus}
                    showSenderName={showSenderName}
                    onRetry={onRetry}
                    onLongPress={onLongPress}
                    onAvatarPress={onAvatarPress}
                />
            </View>
        );
    }, [messages, showAvatar, showMyAvatar, showTime, showStatus, showSenderName, onRetry, onLongPress, onAvatarPress]);

    const keyExtractor = useCallback((item: ChatMessageData) => item.id, []);

    return (
        <DbInfiniteList
            ref={listRef}
            data={messages}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            style={[styles.list, style]}
            contentContainerStyle={styles.contentContainer}
            showsVerticalScrollIndicator={false}
            refreshable={false}
            hasMore={false}
            loadingState="idle"
            headerComponent={ListHeaderComponent}
            footerComponent={ListFooterComponent}
            emptyText="暂无消息"
            noMoreComponent={<></>}
            showScrollToTop={false}
            onScroll={onScroll}
            onScrollBeginDrag={onScrollBeginDrag}
            onScrollEndDrag={onScrollEndDrag}
        />
    );
});

DbChatList.displayName = 'DbChatList';

export default DbChatList;
export type { DbChatListProps, DbChatListRef } from './types';
